import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { selectContextCore, selectContextLanding } from '@store/context/context.selector';
import { IHotelsAppState } from '@store/index';
import { selectSearchBoxParams } from '@store/router/router.selector';
import { Observable, of } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { getNested } from '../../../globals';

interface IDistribution {
  rooms: Array<any>;
  selectedRooms: number;
  total: number;
  totalAdults: number;
  totalChildren: number;
}
const BASE_PATH = '/hotels/results';
@Injectable()
export class SearchService {
  private queryParams: any;
  private isLanding: boolean;
  private HOTEL = 'HOTEL';
  private priceBy = 'NIGHT';
  private city = 'CITY';
  private brand: string;
  constructor(private datePipe: DatePipe, private store$: Store<IHotelsAppState>) {
    this.store$
      .pipe(
        select(selectSearchBoxParams),
        filter((data) => data !== undefined),
      )
      .subscribe((data) => (this.queryParams = data));
    this.store$.pipe(select(selectContextLanding)).subscribe((landing) => {
      this.isLanding = landing;
    });
    this.store$
      .pipe(select(selectContextCore))
      .pipe(take(1))
      .subscribe((context) => {
        this.brand = context.brand;
      });
  }

  private guestsToRooms(guests) {
    return guests.rooms.length
      ? guests.rooms
          .map((room) => {
            return `${room.adults}${
              !!room.children ? '-' + room.childrenAges.map((child) => child.value).join('-') : ''
            }`;
          })
          .join('!')
      : '2';
  }

  private roomsToGuests(rooms): IDistribution {
    const roomsArray = rooms.split('!');
    const distribution: IDistribution = {
      rooms: [],
      selectedRooms: roomsArray.length,
      total: 0,
      totalAdults: 0,
      totalChildren: 0,
    };
    roomsArray.forEach((room) => {
      const dist: any = {};
      const roomArray = room.split('-');
      dist.adults = parseInt(roomArray.shift(), 10);
      dist.children = parseInt(roomArray.length, 10);
      dist.childrenAges = roomArray.map((age) => {
        return {
          value: parseInt(age, 10),
        };
      });
      distribution.totalAdults = distribution.totalAdults + dist.adults;
      distribution.totalChildren = distribution.totalChildren + dist.childrenAges.length;
      distribution.total = distribution.total + dist.adults + dist.childrenAges.length;
      distribution.rooms.push(dist);
    });
    return distribution;
  }

  public prepareSearchInfo(data) {
    if (this.isLanding && !data.date) {
      const checkinDate = new Date();
      checkinDate.setMonth(checkinDate.getMonth() + 1);
      const checkoutDate = new Date(checkinDate);
      checkoutDate.setDate(checkoutDate.getDate() + 1);

      return {
        brand: this.brand,
        checkin: checkinDate.toISOString(),
        checkout: checkoutDate.toISOString(),
        guests: this.roomsToGuests('2'),
        origin: {
          brand: this.brand,
          code: null,
          group: data.type || this.city,
          iata: null,
          id: data.entity.id,
          label: data.entity.name,
          ...(data?.origin?.country_code && {
            country_code: data.origin.country_code,
          }),
        },
      };
    }
    if (!data || !data.date) {
      return;
    }
    const dates = data.date.split(',', 2);
    return {
      brand: this.brand,
      checkin: new Date(dates[0].replace(/-/g, '/')).toISOString(),
      checkout: new Date(dates[1].replace(/-/g, '/')).toISOString(),
      currencyCode: data.currencyCode,
      guests: this.roomsToGuests(data.rooms),
      origin: {
        code: null,
        group: data.type || this.city,
        iata: null,
        id: data.entity.id,
        label: data.entity.name,
        ...(data?.origin?.country_code && {
          country_code: data.origin.country_code,
        }),
      },
    };
  }

  public getNormalizedDates(checkin, checkout) {
    return checkin && checkout
      ? `${this.datePipe.transform(checkin, 'yyyy-MM-dd')},${this.datePipe.transform(
          checkout,
          'yyyy-MM-dd',
        )}`
      : '';
  }

  private getName(newName) {
    const currentName = getNested(this.queryParams, 'entity', 'name');
    const names = (newName || currentName).split(',');
    const destinationName = names.shift();
    return destinationName.toLowerCase().replace(/ /g, '-');
  }

  public searchValidator(data): boolean {
    const newDates = this.getNormalizedDates(
      getNested(data, 'checkin'),
      getNested(data, 'checkout'),
    );
    const haveSameDates = newDates === getNested(this.queryParams, 'date');
    const haveSameIds =
      !this.isLanding &&
      getNested(data, 'origin', 'id') === getNested(this.queryParams, 'entity', 'id');
    const haveSameRooms = this.guestsToRooms(data.guests) === getNested(this.queryParams, 'rooms');
    const haveSameCurerencyCode = data.currencyCode === getNested(this.queryParams, 'currencyCode');
    return !haveSameIds || !haveSameDates || !haveSameRooms || !haveSameCurerencyCode;
  }

  public prepareNavigationData(data): Observable<any> {
    const name = this.getName(data.origin.name);
    const type = (data.origin.group || '').toUpperCase();
    const featuredHotelId = type === this.HOTEL ? data.origin.id : '';
    return of({
      params: {
        brand: this.brand,
        currencyCode: data.currencyCode,
        date: this.getNormalizedDates(data.checkin, data.checkout),
        featuredHotelId,
        priceBy: this.priceBy,
        rooms: this.guestsToRooms(data.guests),
        type: type || this.city,
      },
      path: `${BASE_PATH}/${name + '-' + data.origin.id}`,
    });
  }

  public getHashedId(slugId) {
    slugId = slugId.toString();
    const slugNumber = parseInt(slugId[0], 10);
    const n1 = (slugNumber * 5).toString()[0];
    const n2 = (slugNumber * 7).toString()[0];
    const n3 = (slugNumber * 9).toString()[0];
    const finalSlug = `${slugId}${n1}${n2}${n3}H`;
    return finalSlug;
  }
}
